import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getBoardWiseTicketStats,
  getReportGraphData,
  getReportMetaData,
  getReportsList,
  getReportTableData,
  getTicketFeedbackStats,
  sendAsEmail,
} from "controller/api";
import type {
  GetBoardWiseTicketStatsInputProps,
  GetReportGraphInputProps,
  GetReportTableInputProps,
  GetTicketFeedbackStatsInputProps,
  SendAsEmailInputType,
} from "shared";

/**
 * `resource` can be a keyword or a list. Query keys flatten it to a string so a
 * fresh array literal on every render doesn't read as a different key.
 */
const resourceKey = (resource?: string | Array<string>) =>
  Array.isArray(resource) ? resource.join(",") : resource;

export const useGetReportList = () => {
  return useQuery({
    queryKey: ["reports"],
    queryFn: () => getReportsList(),
  });
};

export const useGetReportMetadata = (
  reportName: string,
  timePeriod: string,
) => {
  return useQuery({
    queryKey: ["reportMetadata", reportName],
    queryFn: () => getReportMetaData(reportName, timePeriod),
    enabled: reportName ? true : false,
  });
};

export const useGetReportGraphData = (
  data: GetReportGraphInputProps,
  /** Lets a caller hold the request until its filters (e.g. resource) resolve. */
  enabled = true,
) => {
  return useQuery({
    queryKey: [
      "reportGraph",
      data.reportName,
      data.timezone,
      data.serviceType,
      data.customer,
      data.timePeriod,
      resourceKey(data.resource),
      data.serviceTypeFilter,
      // GET and POST answer differently on `tickets-by-priority`, so they must
      // not share a cache entry.
      data.method,
    ],
    queryFn: () => getReportGraphData(data),
    enabled: enabled && !!data.reportName,
  });
};

export const useGetReportTableData = (
  data: GetReportTableInputProps,
  /** Lets a caller hold the request until its filters (e.g. resource) resolve. */
  enabled = true,
) => {
  return useQuery({
    queryKey: [
      "reportTable",
      data.reportName,
      data.timezone,
      data.serviceType,
      data.customer,
      data.timePeriod,
      resourceKey(data.resource),
      data.reportType,
      data.serviceTypeFilter,
    ],
    queryFn: () => getReportTableData(data),
    enabled: enabled && !!data.reportName,
  });
};

export const useGetTicketFeedbackStats = (
  data: GetTicketFeedbackStatsInputProps,
  /** Lets a caller hold the request until its filters (e.g. resource) resolve. */
  enabled = true,
) => {
  return useQuery({
    queryKey: [
      "ticketFeedbackStats",
      data.timePeriod,
      data.timezone,
      resourceKey(data.resource),
    ],
    queryFn: () => getTicketFeedbackStats(data),
    enabled,
  });
};

export const useGetBoardWiseTicketStats = (
  data: GetBoardWiseTicketStatsInputProps,
  /** Lets a caller hold the request until its filters (e.g. resource) resolve. */
  enabled = true,
) => {
  return useQuery({
    queryKey: [
      "boardWiseTicketStats",
      data.timePeriod,
      data.timezone,
      resourceKey(data.resource),
    ],
    queryFn: () => getBoardWiseTicketStats(data),
    enabled,
  });
};

export const useSendAsEmail = () => {
  return useMutation((value: SendAsEmailInputType) => sendAsEmail(value), {});
};
